// components/shared/pdf-tools/utils/pdf-document-cache.ts
import { pdfjs } from "react-pdf";

export const urlToBlobUrl = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();

export function getCachedBlobUrl(url: string): string | undefined {
  return urlToBlobUrl.get(url);
}

export function preloadBlobUrlFromBytes(
  url: string,
  bytes: ArrayBuffer,
): string {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const objectUrl = URL.createObjectURL(blob);
  urlToBlobUrl.set(url, objectUrl);
  return objectUrl;
}

export async function fetchAndCacheBlobUrl(url: string): Promise<string> {
  if (urlToBlobUrl.has(url)) return urlToBlobUrl.get(url)!;

  let p = inFlight.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) => r.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        urlToBlobUrl.set(url, objectUrl);
        return objectUrl;
      })
      .finally(() => {
        inFlight.delete(url);
      });
    inFlight.set(url, p);
  }
  return p;
}

type PDFDocumentProxy = Awaited<
  ReturnType<typeof pdfjs.getDocument>["promise"]
>;

type Entry = {
  task: ReturnType<typeof pdfjs.getDocument>;
  promise: Promise<PDFDocumentProxy>;
  resolved?: PDFDocumentProxy;
  refCount: number;
};

const cache = new Map<string, Entry>();

export function acquirePdfDocument(url: string) {
  let entry = cache.get(url);
  if (!entry) {
    const task = pdfjs.getDocument({
      url,
      withCredentials: false,
      disableAutoFetch: true,
      stopAtErrors: true,
      isEvalSupported: false,
    });
    const promise = task.promise;
    entry = { task, promise, refCount: 0 };
    promise
      .then((doc) => {
        entry!.resolved = doc;
      })
      .catch(() => {
        // on failure, drop the entry so future calls can retry
        cache.delete(url);
      });
    cache.set(url, entry);
  }
  entry.refCount++;
  return {
    promise: entry.promise,
    release: () => {
      const e = cache.get(url);
      if (!e) return;
      e.refCount--;
      if (e.refCount <= 0) {
        cache.delete(url);
        try {
          e.resolved?.destroy();
        } catch {}
        try {
          e.task.destroy();
        } catch {}
      }
    },
  };
}
